import React, { useMemo, RefObject, createRef, useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { ExtensionInstance } from '../type'
import { useCheckIconRendered } from '../hooks/useCheckIconRendered'
import Scrollbars from 'react-scrollbars-custom'

type HomePageProps = ExtensionInstance

export const HomePage = (props: HomePageProps): JSX.Element => {
    const { uuid, routes } = props

    const [height, setHeight] = useState<number>(500)

    const mainRef = useRef<HTMLDivElement>(null)
    const iRefs: RefObject<HTMLElement>[] = useMemo(() => routes?.map(() => createRef()), [routes])
    useCheckIconRendered(iRefs)

    useEffect(() => {
        if (mainRef.current) {
            const liveAppHome = document.querySelector('.live-app.home')

            liveAppHome.parentElement.style = 'overflow: hidden'

            setHeight(liveAppHome?.clientHeight)
        }
    }, [mainRef])

    return (
        <Scrollbars
            style={{ width: '100%', height: `${height + 85}px` }}
            disableTrackYWidthCompensation
            removeTracksWhenNotUsed
            mobileNative
            noScrollX
        >
            <Main ref={mainRef} style={{ height: routes?.length < 6 ? `${height}px` : '100%' }}>
                <RadialGradient />
                <Content>
                    <Banner>
                        <img src="/content/plugin-smart-mobility-home-page/banner.png" />
                    </Banner>
                    <Divider>
                        <div className="divider-line left"></div>
                        <span>Quick Menu</span>
                        <div className="divider-line right"></div>
                    </Divider>
                    <QuickMenu>
                        {routes?.map((route, index) => {
                            return (
                                <Card
                                    key={`${uuid}__${index}`}
                                    onClick={() => {
                                        window.location.href = '/#/dashboard/' + route?.dashboardId || '/'
                                    }}
                                >
                                    <span className="card-icon">
                                        <i ref={iRefs[index]} className={route?.icon || 'far fa-circle'} />
                                    </span>
                                    <h3 className="card-title">{route?.title || ''}</h3>
                                    <span className="card-description">{route?.description || ''}</span>
                                </Card>
                            )
                        })}
                    </QuickMenu>
                </Content>
            </Main>
        </Scrollbars>
    )
}

const Main = styled.div`
    * {
        box-sizing: border-box;
    }
    position: relative;
    padding: 150px 0px;
    width: 100%;
    /* height: 100%; */
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    align-items: center;
    background: #fff;
    background-image: url('/content/plugin-smart-mobility-home-page/waves-with-effect.png');
    background-size: cover;
    background-repeat: no-repeat;
`
const Content = styled.div`
    min-width: 848px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    * {
        z-index: 2;
    }
`
const RadialGradient = styled.div`
    background: radial-gradient(ellipse at center, #fff 0%, transparent 80%);
    position: absolute;
    width: 100%;
    max-width: 950px;
    height: 100%;
`
const Banner = styled.div`
    width: 100%;
    max-width: 650px;
    height: 200px;
`

const Divider = styled.div`
    width: 100%;
    max-width: 700px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
    margin-bottom: 40px;

    .divider-line.left {
        flex: 1;
        max-width: 70%;
        height: 2px;
        background: var(--gray-200);
    }
    span {
        font-family: 'Uni Neue', sans-serif;
        font-weight: 700;
        font-size: 16px;
        line-height: 26.11px;
        letter-spacing: 0cap.16px;
        padding: 0 20px;
        width: 150px;
    }
    .divider-line.right {
        flex: 1;
        max-width: 70%;
        height: 2px;
        background: var(--gray-200);
    }
`

const QuickMenu = styled.div`
    width: 100%;
    height: max-content;
    max-width: 1280px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
`

const Card = styled.div`
    position: relative;
    min-width: 200px;
    width: 220px;
    max-width: 220px;
    min-height: 170px;
    max-height: 170px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 8px 8px 8px 20px;

    &::before {
        border-radius: 10px;
        border: 2px solid var(--gray-200);
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: var(--gray-0);
        opacity: 0.7;
    }

    &:hover::before {
        border: 1px solid #b0ca23;
        box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
    }
    &,
    > * {
        cursor: pointer;
    }
    .card-icon {
        color: #b0ca23;
        font-weight: 400;
        font-size: 30px;
        line-height: 59.56px;
    }

    .card-title,
    .card-description {
        color: #384157;
    }

    .card-title {
        font-family: 'Uni Neue', sans-serif;
        font-weight: 700;
        font-size: 18px;
        line-height: 21.6px;
        letter-spacing: 0.2px;
        margin-bottom: 8px;
    }

    .card-description {
        font-family: 'Uni Neue', sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 16.8px;
        letter-spacing: 0.2px;
    }
`
